(() => {
  'use strict';

  const instances = new WeakMap();

  function initialize(root) {
    if (!root || root.dataset.sbBfReady === 'true') return;
    root.dataset.sbBfReady = 'true';
    root.classList.add('js');

    const controller = new AbortController();
    const observers = [];
    const query = selector => root.querySelector(selector);
    const queryAll = selector => [...root.querySelectorAll(selector)];
    const listen = (target, type, callback) => {
      target?.addEventListener(type, callback, { signal: controller.signal });
    };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktopMotion = window.matchMedia('(min-width: 901px) and (hover: hover) and (pointer: fine)');
    const saveData = navigator.connection?.saveData === true;
    const hero = query('.hero');
    const canvas = query('#sb-bf-particles');
    let context;
    const motionButton = query('#sb-bf-motion-toggle');
    let disposed = false;
    let userPaused = false;
    let paused = reducedMotion.matches || !desktopMotion.matches || saveData;
    let heroVisible = true;
    let frameId = 0;
    let previousFrame = 0;
    let elapsed = 0;
    let width = 0;
    let height = 0;
    let motes = [];
    let sparks = [];
    let countdownTimeout;

    function draw(delta) {
      if (!context) return;
      context.clearRect(0, 0, width, height);
      motes.forEach(mote => {
        mote.y -= mote.speed * delta * .055;
        if (mote.y < 0) mote.y = height;
        const alpha = .12 + (Math.sin(elapsed * .0005 + mote.phase) + 1) * .15;
        context.fillStyle = mote.gold ? `rgba(255,203,76,${alpha})` : `rgba(208,185,237,${alpha})`;
        context.beginPath();
        context.arc(mote.x + Math.sin(elapsed * .00015 + mote.phase) * 8, mote.y, mote.radius, 0, Math.PI * 2);
        context.fill();
      });
      sparks = sparks.filter(spark => spark.life > 0);
      sparks.forEach(spark => {
        spark.x += spark.vx * delta * .06;
        spark.y += spark.vy * delta * .06;
        spark.vy += .04 * delta * .06;
        spark.life -= delta;
        context.globalAlpha = Math.max(0, spark.life / 1300);
        context.fillStyle = spark.gold ? '#ffd34e' : '#ce9bff';
        context.save();
        context.translate(spark.x, spark.y);
        context.rotate(spark.life * .004);
        context.fillRect(-spark.size / 2, -spark.size / 2, spark.size, spark.size);
        context.restore();
      });
      context.globalAlpha = 1;
    }

    function resizeCanvas() {
      if (!hero || !canvas || paused || disposed || document.hidden || !heroVisible) return;
      context ||= canvas.getContext('2d');
      if (!context) return;
      const nextWidth = hero.clientWidth;
      const nextHeight = hero.clientHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      if (width === nextWidth && height === nextHeight && canvas.width === Math.floor(nextWidth * ratio)) return;
      width = nextWidth;
      height = nextHeight;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      motes = Array.from({ length: 32 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: Math.random() * 1.2 + .3,
        speed: Math.random() * .18 + .05,
        phase: Math.random() * Math.PI * 2,
        gold: Math.random() > .72
      }));
      draw(0);
    }

    function frame(now) {
      frameId = 0;
      if (disposed || paused || !heroVisible || document.hidden || !root.isConnected || !sparks.length) return;
      const delta = previousFrame ? Math.min(now - previousFrame, 40) : 16;
      previousFrame = now;
      elapsed += delta;
      draw(delta);
      // Only an explicit star click animates the canvas; idle particles are static.
      if (sparks.some(spark => spark.life > 0)) frameId = window.requestAnimationFrame(frame);
      else { sparks = []; draw(0); }
    }

    function synchronizeMotion() {
      if (disposed) return;
      paused = userPaused || reducedMotion.matches || !desktopMotion.matches || saveData;
      root.classList.toggle('motion-paused', paused || document.hidden);
      motionButton?.setAttribute('aria-pressed', String(paused));
      motionButton?.setAttribute('aria-label', paused ? root.dataset.playLabel : root.dataset.pauseLabel);
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = 0;
      previousFrame = 0;
      if (paused) {
        queryAll('[data-countdown]').forEach(field => field.getAnimations?.().forEach(animation => animation.cancel()));
      }
      if (paused || !heroVisible || document.hidden) sparks = [];
      resizeCanvas();
      if (context && sparks.length && !paused && heroVisible && !document.hidden && root.isConnected) {
        frameId = window.requestAnimationFrame(frame);
      }
    }

    listen(motionButton, 'click', () => { userPaused = !userPaused; synchronizeMotion(); });
    listen(reducedMotion, 'change', synchronizeMotion);
    listen(desktopMotion, 'change', synchronizeMotion);
    listen(document, 'visibilitychange', synchronizeMotion);

    const motionZones = [hero, query('.ticker')].filter(Boolean);
    if ('IntersectionObserver' in window) {
      const heroObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          entry.target.classList.toggle('motion-offscreen', !entry.isIntersecting);
          if (entry.target === hero) {
            heroVisible = entry.isIntersecting;
            synchronizeMotion();
          }
        });
      });
      motionZones.forEach(zone => heroObserver.observe(zone));
      observers.push(heroObserver);
    }
    if (hero && 'ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(resizeCanvas);
      resizeObserver.observe(hero);
      observers.push(resizeObserver);
    } else {
      listen(window, 'resize', resizeCanvas);
    }

    queryAll('.energy-star').forEach(button => listen(button, 'click', () => {
      if (!hero || !context || paused || reducedMotion.matches) return;
      const heroRect = hero.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const x = buttonRect.left - heroRect.left + buttonRect.width / 2;
      const y = buttonRect.top - heroRect.top + buttonRect.height / 2;
      for (let index = 0; index < 24; index += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 3.5 + .7;
        sparks.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 1, life: 1300, size: Math.random() * 3 + 1, gold: Math.random() > .35 });
      }
      synchronizeMotion();
    }));

    const revealElements = queryAll('.reveal');
    if ('IntersectionObserver' in window) {
      const revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }), { threshold: .12 });
      revealElements.forEach(element => revealObserver.observe(element));
      observers.push(revealObserver);
    } else {
      revealElements.forEach(element => element.classList.add('is-visible'));
    }

    const signup = query('#sb-bf-early-access');
    const mobileCta = query('#sb-bf-mobile-cta');
    const mobileLayout = window.matchMedia('(max-width: 600px)');
    let pastSignup = false;
    let stickyLayoutFrame = 0;
    let viewportFocusTimeout;
    let observedChatActivator;
    let chatResizeObserver;
    let chatStateObserver;

    function focusedPageField() {
      const active = document.activeElement;
      return root.contains(active) && active?.matches('input:not([type="checkbox"]):not([type="hidden"]), textarea, select, [contenteditable="true"]') ? active : null;
    }

    function updateStickyLayout() {
      stickyLayoutFrame = 0;
      if (disposed || !mobileCta || !root.isConnected) return;
      if (!mobileLayout.matches) {
        mobileCta.classList.remove('visible');
        mobileCta.setAttribute('aria-hidden', 'true');
        mobileCta.inert = true;
        root.classList.remove('bf-input-active', 'bf-chat-open');
        return;
      }
      const field = focusedPageField();
      const chat = mobileLayout.matches ? document.querySelector('shopify-chat[view="chat"]') : null;
      const activator = chat?.shadowRoot?.querySelector('button[part="activator"]');
      let chatOpen = false;
      let leftClearance = 14;
      let rightClearance = chat ? 128 : 14;
      let bottomClearance = 14;
      if (activator) {
        const rect = activator.getBoundingClientRect();
        chatOpen = activator.getAttribute('aria-expanded') === 'true';
        if (rect.width > 0 && rect.height > 0) {
          if (rect.left >= window.innerWidth / 2) rightClearance = Math.ceil(window.innerWidth - rect.left + 12);
          else { leftClearance = Math.ceil(rect.right + 12); rightClearance = 14; }
          // A wide launcher must not squeeze the signup CTA into a narrow sliver.
          if (window.innerWidth - leftClearance - rightClearance < 200) {
            leftClearance = rightClearance = 14;
            bottomClearance = Math.max(14, Math.ceil(window.innerHeight - rect.top + 12));
          }
        }
        if (observedChatActivator !== activator) {
          chatResizeObserver?.disconnect();
          chatStateObserver?.disconnect();
          observedChatActivator = activator;
          if ('ResizeObserver' in window) {
            chatResizeObserver = new ResizeObserver(scheduleStickyLayout);
            chatResizeObserver.observe(activator);
            observers.push(chatResizeObserver);
          }
          if ('MutationObserver' in window) {
            chatStateObserver = new MutationObserver(scheduleStickyLayout);
            chatStateObserver.observe(activator, { attributes: true, attributeFilter: ['aria-expanded', 'style', 'class'] });
            observers.push(chatStateObserver);
          }
        }
      }
      const nextLeft = `${leftClearance}px`;
      const nextRight = `${rightClearance}px`;
      const nextBottom = `${bottomClearance}px`;
      if (root.style.getPropertyValue('--sb-bf-sticky-left') !== nextLeft) root.style.setProperty('--sb-bf-sticky-left', nextLeft);
      if (root.style.getPropertyValue('--sb-bf-sticky-right') !== nextRight) root.style.setProperty('--sb-bf-sticky-right', nextRight);
      if (root.style.getPropertyValue('--sb-bf-sticky-bottom') !== nextBottom) root.style.setProperty('--sb-bf-sticky-bottom', nextBottom);
      root.classList.toggle('bf-input-active', Boolean(field));
      root.classList.toggle('bf-chat-open', chatOpen);
      const visible = mobileLayout.matches && pastSignup && !field && !chatOpen && !query('dialog[open]');
      mobileCta.classList.toggle('visible', visible);
      if (mobileCta.getAttribute('aria-hidden') !== String(!visible)) mobileCta.setAttribute('aria-hidden', String(!visible));
      if (mobileCta.inert !== !visible) mobileCta.inert = !visible;
    }

    function scheduleStickyLayout() {
      if (!disposed && !document.hidden && mobileCta && !stickyLayoutFrame) stickyLayoutFrame = window.requestAnimationFrame(updateStickyLayout);
    }

    function keepFocusedFieldVisible() {
      window.clearTimeout(viewportFocusTimeout);
      scheduleStickyLayout();
      if (!mobileLayout.matches || !focusedPageField()) return;
      viewportFocusTimeout = window.setTimeout(() => {
        const field = focusedPageField();
        if (disposed || !field) return;
        const viewport = window.visualViewport;
        const top = viewport?.offsetTop || 0;
        const bottom = top + (viewport?.height || window.innerHeight);
        const rect = field.getBoundingClientRect();
        if (rect.top < top + 80 || rect.bottom > bottom - 88) field.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
      }, 160);
    }

    if (signup && mobileCta && 'IntersectionObserver' in window) {
      const signupObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        pastSignup = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        scheduleStickyLayout();
      });
      signupObserver.observe(signup);
      observers.push(signupObserver);
    }
    listen(root, 'focusin', keepFocusedFieldVisible);
    listen(root, 'focusout', scheduleStickyLayout);
    listen(window, 'resize', keepFocusedFieldVisible);
    listen(window.visualViewport, 'resize', keepFocusedFieldVisible);
    listen(window.visualViewport, 'scroll', () => { if (focusedPageField()) scheduleStickyLayout(); });
    listen(mobileLayout, 'change', scheduleStickyLayout);
    listen(document, 'click', event => {
      if (root.contains(event.target) || event.target.closest?.('shopify-chat')) scheduleStickyLayout();
    });
    listen(mobileCta, 'click', event => {
      if (!signup || !event.target.closest('a[href="#sb-bf-early-access"]') || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      signup.scrollIntoView({ behavior: paused ? 'auto' : 'smooth', block: 'center' });
      query('#sb-bf-email')?.focus({ preventScroll: true });
      keepFocusedFieldVisible();
    });
    if ('MutationObserver' in window) {
      const chatMountObserver = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('shopify-chat') || node.querySelector?.('shopify-chat'))))) scheduleStickyLayout();
      });
      chatMountObserver.observe(document.body, { childList: true, subtree: true });
      observers.push(chatMountObserver);
    }
    scheduleStickyLayout();

    queryAll('.dialog-close').forEach(button => listen(button, 'click', () => {
      const modal = button.closest('dialog');
      if (modal && root.contains(modal)) modal.close();
    }));
    queryAll('dialog').forEach(modal => listen(modal, 'click', event => {
      if (event.target !== modal) return;
      const rect = modal.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) modal.close();
    }));

    // Mystery cards are native anchors to the access panel: no extra dialog or
    // forced email focus, including for keyboard and modified-click navigation.
    const mysteryDialog = query('#sb-bf-mystery-dialog');
    listen(query('#sb-bf-mystery-signup'), 'click', event => {
      if (!signup) return;
      event.preventDefault();
      mysteryDialog?.close();
      signup.scrollIntoView({ behavior: paused ? 'auto' : 'smooth', block: 'center' });
      const focusTarget = query('#sb-bf-email') || signup.querySelector('[role="status"], [role="alert"]');
      focusTarget?.focus({ preventScroll: true });
      if (!paused) {
        signup.classList.remove('signup-highlight');
        void signup.offsetWidth;
        signup.classList.add('signup-highlight');
      }
    });

    const countdown = query('#sb-bf-countdown');
    const launchTime = query('#sb-bf-launch-time');
    const deadline = Date.parse(launchTime?.dateTime || '');
    const clock = countdown?.querySelector('[role="timer"]');
    const caption = query('#sb-bf-countdown-caption');
    const status = query('#sb-bf-countdown-status');
    const fields = countdown ? [...countdown.querySelectorAll('[data-countdown]')] : [];
    let expired = false;
    let countdownInitialized = false;

    function updateCountdown() {
      window.clearTimeout(countdownTimeout);
      if (disposed || !root.isConnected || !countdown || !clock || !Number.isFinite(deadline)) return;
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));
      const values = { days: Math.floor(remaining / 86400), hours: Math.floor(remaining / 3600) % 24, minutes: Math.floor(remaining / 60) % 60, seconds: remaining % 60 };
      fields.forEach(field => {
        const value = String(values[field.dataset.countdown]).padStart(2, '0');
        if (field.textContent === value) return;
        field.textContent = value;
        if (countdownInitialized && !paused && heroVisible && !reducedMotion.matches && !document.hidden && typeof field.animate === 'function') {
          field.getAnimations?.().forEach(animation => animation.cancel());
          field.animate([{ opacity: .5, transform: 'translateY(3px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 180, easing: 'ease-out' });
        }
      });
      clock.hidden = false;
      countdown.classList.toggle('countdown-expired', remaining === 0);
      if (caption) caption.textContent = remaining ? root.dataset.countdownCaption : root.dataset.countdownExpired;
      if (status && !remaining && !expired) status.textContent = root.dataset.countdownStatus;
      expired = remaining === 0;
      countdownInitialized = true;
      if (remaining && !document.hidden) countdownTimeout = window.setTimeout(updateCountdown, Math.min(1000, deadline - now));
    }

    listen(document, 'visibilitychange', updateCountdown);
    listen(window, 'pageshow', updateCountdown);
    listen(window, 'pageshow', synchronizeMotion);
    listen(window, 'pageshow', scheduleStickyLayout);
    listen(window, 'pagehide', () => {
      window.clearTimeout(countdownTimeout);
      window.clearTimeout(viewportFocusTimeout);
      if (stickyLayoutFrame) window.cancelAnimationFrame(stickyLayoutFrame);
      stickyLayoutFrame = 0;
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = 0;
      previousFrame = 0;
    });

    instances.set(root, () => {
      disposed = true;
      controller.abort();
      observers.forEach(observer => observer.disconnect());
      window.clearTimeout(countdownTimeout);
      window.clearTimeout(viewportFocusTimeout);
      if (stickyLayoutFrame) window.cancelAnimationFrame(stickyLayoutFrame);
      if (frameId) window.cancelAnimationFrame(frameId);
      fields.forEach(field => field.getAnimations?.().forEach(animation => animation.cancel()));
      root.classList.remove('js', 'motion-paused', 'bf-input-active', 'bf-chat-open');
      motionZones.forEach(zone => zone.classList.remove('motion-offscreen'));
      root.style.removeProperty('--sb-bf-sticky-left');
      root.style.removeProperty('--sb-bf-sticky-right');
      root.style.removeProperty('--sb-bf-sticky-bottom');
      delete root.dataset.sbBfReady;
      instances.delete(root);
    });

    resizeCanvas();
    synchronizeMotion();
    updateCountdown();
  }

  function findRoot(container) {
    return container?.matches?.('#sb-early-access') ? container : container?.querySelector?.('#sb-early-access');
  }

  initialize(document.getElementById('sb-early-access'));
  document.addEventListener('shopify:section:load', event => initialize(findRoot(event.target)));
  document.addEventListener('shopify:section:unload', event => {
    const root = findRoot(event.target);
    if (root) instances.get(root)?.();
  });
})();
